import { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import EmailIcon from "@mui/icons-material/Email";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Colors } from "Components/Layout/Themes/Colors";
import type { TransactionFilters } from "Components/Transactions/hooks/useTransactionFilterManager";
import { ExportDrawer } from "Components/Transactions/ExportDrawer";
import { Resources, type TFunction, useResource } from "Translations/Resources";
import { useExportTransactionsMutation } from "Api/Mutations/Transactions/useExportTransactionsMutation";
import { filtersToQueryParams } from "Components/Transactions/utils/transactionFilterUtils";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";

const PageResources = Resources.Components.Transactions;
const DrawerResources = Resources.Components.Transactions.ExportDrawer;

const getEmailSchema = (t: TFunction) =>
  z.object({
    email: z.email({ message: t(Resources.Validation.InvalidEmail) }),
  });

type EmailFormData = z.infer<ReturnType<typeof getEmailSchema>>;

type Props = {
  filters: TransactionFilters;
};

export const ExportActions = ({ filters }: Props) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);

  const { t } = useResource();
  const theme = useTheme();
  const { mutate: exportTransactions, isPending } =
    useExportTransactionsMutation();
  const emailSchema = useMemo(() => getEmailSchema(t), [t]);

  const {
    control,
    handleSubmit,
    reset: resetEmailForm,
  } = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: "" },
  });

  const handleOpenEmailDialog = () => {
    setIsEmailDialogOpen(true);
    resetEmailForm();
  };

  const handleCloseEmailDialog = () => {
    setIsEmailDialogOpen(false);
  };

  const handleSendEmail = ({ email }: EmailFormData) => {
    exportTransactions(
      {
        format: "Email",
        filters: filtersToQueryParams(filters),
        email,
      },
      {
        onSuccess: () => {
          toast.success(t(DrawerResources.EmailSentSuccess));
          handleCloseEmailDialog();
        },
        onError: () => {
          toast.error(t(DrawerResources.EmailSentError));
        },
      },
    );
  };

  return (
    <Box display="flex" gap={1}>
      <IconButton
        onClick={handleOpenEmailDialog}
        size="small"
        sx={{
          color: theme.palette.primary.main,
          "&:hover": { backgroundColor: Colors.MediumGrey },
        }}
        aria-label={t(PageResources.ExportActions.Email)}
      >
        <EmailIcon />
      </IconButton>
      <IconButton
        onClick={() => setIsDrawerOpen(true)}
        size="small"
        sx={{
          color: theme.palette.primary.main,
          "&:hover": { backgroundColor: Colors.MediumGrey },
        }}
        aria-label={t(PageResources.ExportActions.Download)}
      >
        <DownloadIcon />
      </IconButton>
      <ExportDrawer
        isOpen={isDrawerOpen}
        onOpen={() => setIsDrawerOpen(true)}
        onClose={() => setIsDrawerOpen(false)}
        filters={filters}
      />
      <Dialog
        open={isEmailDialogOpen}
        onClose={handleCloseEmailDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t(DrawerResources.SendToEmail)}</DialogTitle>
        <Box component="form" onSubmit={handleSubmit(handleSendEmail)}>
          <DialogContent>
            <FormInput
              autoFocus
              control={control}
              name="email"
              label={t(DrawerResources.EmailAddress)}
              placeholder={t(DrawerResources.EnterEmailAddress)}
              type="email"
              valueTransformer={(v) => v.toLowerCase()}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEmailDialog}>
              {t(DrawerResources.Cancel)}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isPending}
              startIcon={isPending ? <CircularProgress size={20} /> : undefined}
            >
              {t(DrawerResources.Send)}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};
